/**
 * @file look-warnings.ts
 * @description Предупреждения об оформлении секции в кабинете (phase19.16,
 * партия SB120, этап 1; site-builder.md SB120 «закон и доступность — как
 * предупреждение»; проект uiux-sb120-stage1 §6, данные
 * db-design-sb120-stage1.md §4 — `sectionLookWarnings` из shared).
 *
 * Здесь — что кабинету нужно вокруг чистой функции shared: какие файлы
 * секции измерить (фон ПК и телефона, фото в колонках), тема для расчёта
 * (плотность и доля телефона; пока оформление не пришло — «Обычно» и 0,6) и
 * общий список «замечания + предупреждения» для `SectionIssueList`
 * (SB120 этап 3а — и предупреждения об элементах, element-warnings.ts).
 */

import {
  type SectionLookWarningCode,
  SECTION_LOOK_LIMITS,
  SITE_THEME_LIMITS,
  SECTION_LOOK_WARNING_MESSAGES,
  sectionLookWarnings,
  type SectionElementWarning,
  type SectionMediaSizes,
  type SectionLookWarning,
  type SectionPayload,
  type SectionWarningTheme,
  type SiteTheme,
} from "@chaiberi/shared";

import type { SectionIssueItem } from "../../components/site-builder/section/SectionIssueList";
import { formatNumber } from "../site-fields/number-input";
import { elementWarningText } from "./element-warnings";

/**
 * Справочные предупреждения — не «замечание», а пояснение под своим полем
 * (решение оркестратора 2026-10-18): не входят в счётчик «Есть
 * предупреждения (N)», не ставят «!» у группы и не попадают в общий список
 * — показываются только подсказкой под полем (`LookWarningNote`).
 * `text_on_photo` («проверьте на канве») есть почти у каждого героя с фото,
 * и в общем списке он заслонял бы настоящие предупреждения.
 */
export const LOOK_WARNING_REFERENCE_CODES: readonly SectionLookWarningCode[] = ["text_on_photo"];

/** Справочное предупреждение (только подсказка под полем). */
export function isReferenceWarning(warning: Pick<SectionLookWarning, "code">): boolean {
  return LOOK_WARNING_REFERENCE_CODES.includes(warning.code);
}

/** Предупреждения для счётчика, «!» у группы и общего списка — без справочных. */
export function attentionWarnings(warnings: readonly SectionLookWarning[]): SectionLookWarning[] {
  return warnings.filter((w) => !isReferenceWarning(w));
}

/** Файлы секции, размеры которых нужны предупреждениям и расчётам «По фото». */
export function sectionMediaIds(payload: SectionPayload): string[] {
  const ids: string[] = [];
  const bg = payload.look.bg;
  if (bg.kind === "media") {
    ids.push(bg.mediaId);
    if (bg.mediaIdPhone) ids.push(bg.mediaIdPhone);
  }
  for (const column of payload.columns.items) {
    for (const el of column.elements) if (el.kind === "image" && el.mediaId) ids.push(el.mediaId);
  }
  return ids;
}

/** Плотность и доля телефона из оформления сайта; нет оформления — «Обычно» и 0,6. */
export function warningTheme(theme: SiteTheme | null): SectionWarningTheme {
  return {
    spacing: {
      density: theme?.spacing.density ?? SITE_THEME_LIMITS.density.default,
      phoneFactor: theme?.spacing.phoneFactor ?? SECTION_LOOK_LIMITS.phoneFactor.default,
    },
  };
}

/** Где кадр фото по пути предупреждения: фон ПК, фон телефона или колонка. */
function frameWhere(path: string): string {
  if (path.endsWith("mediaIdPhone")) return "на телефоне";
  return path.startsWith("columns.") ? "колонки на ПК" : "на ПК";
}

/** Подробности для текста: ширина файла (если известна). */
export interface LookWarningExtra {
  fileWidth?: number | null;
}

type WarningText = (value: number | undefined, path: string, extra: LookWarningExtra) => string | null;

/**
 * Свой текст на каждый код — с числами (uiux-sb120-stage1 §6, §2.4); `null` —
 * числа нет, берётся общий текст shared (`SECTION_LOOK_WARNING_MESSAGES`).
 */
const WARNING_TEXT: Readonly<Record<SectionLookWarningCode, WarningText>> = {
  phone_side_lt12: (v) => (v === undefined ? null : `Меньше 12 px по бокам — на телефоне ${formatNumber(v, 1)} px: текст прилипнет к краю экрана. Можно оставить.`),
  phone_taller_than_screen: (v) => (v === undefined ? null : `На телефоне блок ${v} px — выше экрана: кнопку увидят только после прокрутки.`),
  width_lt480: (v) => (v === undefined ? null : `Ширина ${v} px — уже 480 px: строки станут короткими.`),
  narrow_column: (v) => (v === undefined ? null : `Колонка около ${v} px на ПК — по 2–3 слова в строке.`),
  photo_smaller_than_frame: (v, path, extra) => {
    if (v === undefined) return null;
    const file = extra.fileWidth ? `Фото ${extra.fileWidth} px` : "Фото";
    return `${file} уже кадра ${frameWhere(path)} (${v} px) — может быть размыто.`;
  },
  photo_band_phone: (v) => (v === undefined ? null : `«По фото» на телефоне — полоса ${v} px, текст не поместится. Лучше кадр 4:5 или «Экран».`),
  text_on_photo: () => null,
};

/** Текст предупреждения словами владельца, с числами, если они есть. */
export function lookWarningText(warning: SectionLookWarning, extra: LookWarningExtra = {}): string {
  return WARNING_TEXT[warning.code](warning.value, warning.path, extra) ?? SECTION_LOOK_WARNING_MESSAGES[warning.code];
}

/**
 * Замечания проверки (ошибки), предупреждения оформления и элементов — одним
 * списком (справочные — нет). Одинаковые строки элементов («больше 3
 * шрифтов» у разных частей) — один раз.
 */
export function sectionIssueItems(
  issues: ReadonlyArray<{ path: string; message: string }>,
  warnings: readonly SectionLookWarning[],
  elementWarnings: readonly SectionElementWarning[] = [],
): SectionIssueItem[] {
  // Удалённый свой шрифт — ✕ (по проекту), остальное — предупреждения; сохранение не блокируют.
  const elementItems = elementWarnings.map((w) => ({ path: w.path, message: elementWarningText(w), tone: w.code === "element_font_missing" ? ("error" as const) : ("warning" as const) }));
  const seen = new Set<string>();
  return [
    ...issues.map((i) => ({ path: i.path, message: i.message, tone: "error" as const })),
    // Пара «слева и справа» с одним числом — одна строка (QA D3).
    ...uniqueByText(attentionWarnings(warnings)).map((w) => ({ path: w.path, message: lookWarningText(w), tone: "warning" as const })),
    ...elementItems.filter((item) => !seen.has(item.message) && seen.add(item.message)),
  ];
}

/** Короткая причина для сводки группы: «Фото · 30 % · фото мельче кадра». */
const WARNING_SHORT: Readonly<Partial<Record<SectionLookWarningCode, string>>> = {
  photo_smaller_than_frame: "фото мельче кадра",
  photo_band_phone: "низкая полоса на телефоне",
  phone_taller_than_screen: "выше экрана телефона",
  width_lt480: "узкий блок",
  phone_side_lt12: "мало по бокам",
  narrow_column: "узкая колонка",
};

/** Причина «!» у группы по префиксу путей (первое предупреждение); `null` — нет. */
export function warningReason(warnings: readonly SectionLookWarning[], prefix: string): string | null {
  const found = attentionWarnings(warnings).find((w) => w.path === prefix || w.path.startsWith(`${prefix}.`));
  return found ? (WARNING_SHORT[found.code] ?? null) : null;
}

/** Одинаковые по тексту предупреждения (слева и справа с одним числом) — один раз. */
export function uniqueByText(warnings: readonly SectionLookWarning[]): SectionLookWarning[] {
  const seen = new Set<string>();
  return warnings.filter((w) => {
    const text = lookWarningText(w);
    if (seen.has(text)) return false;
    seen.add(text);
    return true;
  });
}

/** Предупреждения по путям поля (точное совпадение) — под самим полем. */
export function warningsAtPaths(warnings: readonly SectionLookWarning[], paths: readonly string[]): SectionLookWarning[] {
  return warnings.filter((w) => paths.includes(w.path));
}

/**
 * «Фото … уже кадра — может быть размыто» для шага «Кадр под блок» (uiux
 * §2.4): те же проверки shared на секции с черновиком кадра; колонки на
 * предупреждения фона не влияют, поэтому берётся пустая сетка.
 */
export function frameWarnings(look: SectionPayload["look"], theme: SiteTheme | null, sizes: SectionMediaSizes): SectionLookWarning[] {
  const payload: SectionPayload = { columns: { shares: [12], valign: "center", items: [{ elements: [] }] }, look };
  try {
    return sectionLookWarnings(payload, warningTheme(theme), sizes).filter((w) => w.code === "photo_smaller_than_frame");
  } catch {
    return [];
  }
}
